import React, { useState } from 'react';
import {
  lookupFamilyCode,
  setStoredFamilyCode,
} from '../services/familyCode.ts';
import { signInAnonymousKid } from '../services/auth.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '../fa.ts';

interface KidCodeScreenProps {
  onSuccess: (familyId: string) => void;
  onBack: () => void;
}

export default function KidCodeScreen(
  props: KidCodeScreenProps
): React.ReactElement {
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (val.length <= 6) {
      setCode(val);
      setErr(null);
    }
  };

  const handleSubmit = async () => {
    if (code.length < 6) {
      setErr('Code must be 6 characters');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const familyId = await lookupFamilyCode(code);
      if (!familyId) {
        setErr('Invalid family code. Ask your parent for the code.');
        setBusy(false);
        return;
      }
      await setStoredFamilyCode(code);
      await signInAnonymousKid();
      props.onSuccess(familyId);
    } catch (_e) {
      setErr('Something went wrong. Try again.');
      setBusy(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !busy) {
      handleSubmit();
    }
  };

  return (
    <main className='page-wrapper page-centered'>
      <h1 className='font-display text-5xl font-bold text-qslate tracking-wider mb-4'>
        LOOTBOUND
      </h1>
      <div className='text-base text-qmuted mb-5'>Enter your family code</div>

      <div className='bg-qyellow w-full max-w-[360px] p-6 rounded-card flex flex-col items-center gap-5'>
        <label htmlFor='family-code' className='sr-only'>
          Family code
        </label>
        <input
          id='family-code'
          type='text'
          value={code}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder='ABC123'
          maxLength={6}
          autoCapitalize='characters'
          autoComplete='off'
          className='quest-input text-center text-[28px] font-display font-bold tracking-[8px] py-3.5 px-4'
        />

        <div className='text-[13px] text-qslate text-center'>
          Ask your parent for this code
        </div>

        <div
          role={err ? 'alert' : undefined}
          className='text-qcoral text-[13px] text-center min-h-[20px]'
        >
          {err}
        </div>

        <button
          onClick={handleSubmit}
          disabled={busy || code.length < 6}
          className='btn-primary w-full disabled:cursor-not-allowed'
        >
          {busy ? 'Connecting...' : 'Connect'}
        </button>

        <button onClick={props.onBack} disabled={busy} className='btn-ghost'>
          <FontAwesomeIcon icon={faAngleLeft} className='mr-1.5' />
          Back
        </button>
      </div>
    </main>
  );
}
